import React from "react";

interface FullDoughnutChartProps {
  /** Score value (0-100 for percentage, or actual value if using scale) */
  value: number;
  /** Maximum value (100 for percentage, or scaleMax if using scale) */
  maxValue: number;
  /** Hex color code (with #) */
  color: string;
  /** Text to display in the center */
  displayText: string;
  /** Optional label under the score */
  label?: string;
  /** Whether a sparkline is shown below (affects bottom padding) */
  hasSparkline?: boolean;
}

const FullDoughnutChart: React.FC<FullDoughnutChartProps> = ({
  value,
  maxValue,
  color,
  displayText,
  label,
  hasSparkline = false,
}) => {
  // Calculate percentage for the arc (0-100)
  const percentage = Math.min(100, Math.max(0, (value / maxValue) * 100));

  // SVG configuration - slightly smaller than half doughnut to maintain similar footprint
  const size = 140;
  const strokeWidth = 14;
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;

  // Calculate the circumference of the full circle
  const circumference = 2 * Math.PI * radius;

  // Calculate the stroke-dasharray for the percentage
  const progress = (percentage / 100) * circumference;

  return (
    <div
      className={`w-full flex flex-col items-center justify-center px-4 ${
        hasSparkline ? "pb-2" : "pb-4"
      }`}
    >
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="w-full max-w-[140px]"
        style={{ height: "auto" }}
      >
        {/* Background full circle (gray) */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
        />

        {/* Filled circle (colored) - starts from top and goes clockwise */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${progress} ${circumference}`}
          style={{
            transform: "rotate(-90deg)",
            transformOrigin: "center",
            transition: "stroke-dasharray 0.3s ease",
          }}
        />

        {/* Center text - Score */}
        <text
          x={center}
          y={label ? center - 7 : center}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            fontSize: "26px",
            fontWeight: "bold",
            fill: "#111827",
          }}
        >
          {displayText.includes("%") ? (
            <>
              <tspan>{displayText.replace("%", "")}</tspan>
              <tspan
                style={{
                  fontSize: "16px",
                  fontWeight: "normal",
                  fill: "#6B7280",
                }}
              >
                %
              </tspan>
            </>
          ) : (
            displayText
          )}
        </text>

        {/* Center text - Label */}
        {label && (
          <text
            x={center}
            y={center + 12}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{
              fontSize: "9px",
              fill: "#6B7280",
            }}
          >
            {label}
          </text>
        )}
      </svg>
    </div>
  );
};

export default FullDoughnutChart;
